/**
 * DataTable — projedeki tek tablo bileşeni.
 *
 * Her liste ekranı bu bileşeni kullanır; değişen tek şey kolon tanımlarıdır.
 * Tablo iskeleti (başlık bandı, sıralama, satır içi filtre, boş durum, toplam
 * satırı, kaydırma) her yerde aynıdır.
 *
 * ── Kolon tanımı ────────────────────────────────────────────────────────────
 *   {
 *     anahtar:  'adSoyad',                 // satırdaki alan adı (zorunlu)
 *     baslik:   'Ad Soyad',                // başlıkta görünen metin
 *     baslikCiz:() => node,                // başlığın tamamının yerine geçer (tümünü seç kutusu gibi)
 *     baslikAlti:() => node,               // başlığın altına ek içerik (sayfa düzeyi filtre kutusu)
 *     hizala:   'sol' | 'orta' | 'sag',    // varsayılan 'sol'
 *     genislik: 'w-32',                    // opsiyonel Tailwind genişlik sınıfı (min-w- da olabilir)
 *     siralanir:  true,                    // başlığa tıklayınca sıralasın
 *     filtrelenir:true,                    // başlığın altına arama kutusu koysun
 *     deger:    (satir) => ...,            // sıralama/filtre için ham değer
 *     ciz:      (satir, indeks) => node,   // hücre içeriği (rozet, progress bar…)
 *     toplam:   true | (satirlar) => node, // toplam satırında gösterilecek
 *   }
 *
 * ── Kullanım ────────────────────────────────────────────────────────────────
 *   <DataTable
 *     kolonlar={KOLONLAR}
 *     satirlar={liste}
 *     anahtarAlan="rezervasyonId"
 *     bosIkon="🗓️"
 *     bosMetin="Kayıt bulunamadı"
 *     toplamGoster
 *     onSatirTikla={r => setDetay(r)}
 *   />
 *
 * Sayfalama bu bileşenin işi değildir; sayfa kendi `Pagination` bileşenini
 * tablonun altına koyar ve dilimlenmiş listeyi `satirlar` olarak verir.
 */

import { useState, useMemo, Fragment } from 'react';
import EmptyState from './EmptyState';

const HIZA = { sol: 'text-left', orta: 'text-center', sag: 'text-right' };

// Kolonun ham değeri: özel `deger` varsa o, yoksa alanın kendisi
function hamDeger(kolon, satir) {
  if (typeof kolon.deger === 'function') return kolon.deger(satir);
  return satir?.[kolon.anahtar];
}

function metneCevir(v) {
  if (v === null || v === undefined) return '';
  return String(v).toLocaleLowerCase('tr-TR');
}

export default function DataTable({
  kolonlar = [],
  satirlar = [],
  anahtarAlan,
  bosIkon = '📭',
  bosMetin = 'Kayıt bulunamadı',
  toplamGoster = false,
  toplamEtiketi = 'TOPLAM',
  onSatirTikla,
  satirSinifi,           // (satir, indeks) => ek Tailwind sınıfı
  satirStil,             // (satir, indeks) => inline style (Tailwind ile verilemeyen renkler için)
  detaySatiri,           // (satir) => node | null — dolu dönerse altına tam genişlik satır açılır
  yogun = false,         // dar satır yüksekliği
  enAzGenislik,          // '1650px' — kolonlar sıkışmasın diye tablo en az bu genişlikte olur
  className = '',
}) {
  const [sirala, setSirala] = useState(null);        // { anahtar, yon: 'asc' | 'desc' }
  const [filtreler, setFiltreler] = useState({});    // { anahtar: 'metin' }

  const filtreliVarMi = kolonlar.some(k => k.filtrelenir);

  const gorunen = useMemo(() => {
    let liste = satirlar;

    // Satır içi filtreler
    const aktifler = Object.entries(filtreler).filter(([, v]) => v?.trim());
    if (aktifler.length > 0) {
      liste = liste.filter(satir =>
        aktifler.every(([anahtar, metin]) => {
          const kolon = kolonlar.find(k => k.anahtar === anahtar);
          if (!kolon) return true;
          return metneCevir(hamDeger(kolon, satir)).includes(metin.trim().toLocaleLowerCase('tr-TR'));
        })
      );
    }

    // Sıralama — sayısal değerlerde sayıya göre, diğerlerinde Türkçe alfabetik
    if (sirala) {
      const kolon = kolonlar.find(k => k.anahtar === sirala.anahtar);
      if (kolon) {
        liste = [...liste].sort((a, b) => {
          const x = hamDeger(kolon, a);
          const y = hamDeger(kolon, b);
          let k;
          if (typeof x === 'number' && typeof y === 'number') k = x - y;
          else if (x instanceof Date && y instanceof Date) k = x - y;
          else k = String(x ?? '').localeCompare(String(y ?? ''), 'tr-TR', { numeric: true });
          return sirala.yon === 'asc' ? k : -k;
        });
      }
    }

    return liste;
  }, [satirlar, filtreler, sirala, kolonlar]);

  const basligaTikla = (kolon) => {
    if (!kolon.siralanir) return;
    setSirala(p =>
      p?.anahtar !== kolon.anahtar ? { anahtar: kolon.anahtar, yon: 'asc' }
      : p.yon === 'asc' ? { anahtar: kolon.anahtar, yon: 'desc' }
      : null
    );
  };

  const hucreDolgu = yogun ? 'px-4 py-2.5' : 'px-6 py-4';

  return (
    <div className={`overflow-x-auto ${className}`}>
      <table className="w-full text-left border-collapse"
        style={enAzGenislik ? { minWidth: enAzGenislik } : undefined}>
        <thead>
          <tr className="bg-gray-50/80 border-b border-gray-100">
            {kolonlar.map(k => {
              const aktif = sirala?.anahtar === k.anahtar;
              return (
                <th key={k.anahtar}
                  className={`${hucreDolgu} ${HIZA[k.hizala] || HIZA.sol} ${k.genislik || ''}
                              text-[10px] font-black uppercase tracking-widest text-gray-400 align-top`}>
                  {/* baslikCiz: başlığın tamamının yerine geçer (tümünü seç kutusu gibi) */}
                  {k.baslikCiz ? k.baslikCiz() : (
                    <span
                      onClick={() => basligaTikla(k)}
                      className={`inline-flex items-center whitespace-nowrap
                                  ${k.siralanir ? 'cursor-pointer select-none hover:text-gray-600 transition-colors' : ''}`}>
                      {k.baslik}
                      {k.siralanir && (
                        <span className={`ml-1.5 ${aktif ? 'text-violet-500' : 'text-gray-300'}`}>
                          {aktif ? (sirala.yon === 'asc' ? '↑' : '↓') : '⇅'}
                        </span>
                      )}
                    </span>
                  )}

                  {/* baslikAlti: başlığın altına ek içerik (sayfa düzeyi filtre kutusu).
                      Tıklama başlığa sıçramaz; aksi halde filtreye tıklamak sıralama yapardı. */}
                  {k.baslikAlti && (
                    <div className="mt-2.5 normal-case tracking-normal font-normal"
                      onClick={e => e.stopPropagation()}>
                      {k.baslikAlti()}
                    </div>
                  )}
                </th>
              );
            })}
          </tr>

          {/* Satır içi filtre bandı — yalnızca filtrelenebilir kolon varsa */}
          {filtreliVarMi && (
            <tr className="border-b border-gray-100">
              {kolonlar.map(k => (
                <th key={k.anahtar} className="px-3 py-2">
                  {k.filtrelenir && (
                    <input
                      value={filtreler[k.anahtar] || ''}
                      onChange={e => setFiltreler(p => ({ ...p, [k.anahtar]: e.target.value }))}
                      placeholder="Ara…"
                      className="w-full px-3 py-2 rounded-xl border-2 border-gray-100 bg-white text-[11px]
                                 font-semibold text-gray-700 placeholder-gray-300 outline-none
                                 focus:border-violet-300 transition-colors"
                    />
                  )}
                </th>
              ))}
            </tr>
          )}
        </thead>

        <tbody className="divide-y divide-gray-100">
          {gorunen.length === 0 ? (
            <tr>
              <td colSpan={kolonlar.length}>
                <EmptyState icon={bosIkon} text={bosMetin} />
              </td>
            </tr>
          ) : (
            gorunen.map((satir, i) => {
              const anahtar = anahtarAlan ? satir[anahtarAlan] ?? i : i;
              const detay = detaySatiri ? detaySatiri(satir) : null;

              return (
                <Fragment key={anahtar}>
                  <tr
                    onClick={onSatirTikla ? () => onSatirTikla(satir) : undefined}
                    style={satirStil ? satirStil(satir, i) : undefined}
                    className={`transition-colors hover:bg-violet-50/40
                                ${onSatirTikla ? 'cursor-pointer' : ''}
                                ${satirSinifi ? satirSinifi(satir, i) : ''}`}>
                    {kolonlar.map(k => (
                      <td key={k.anahtar}
                        className={`${hucreDolgu} ${HIZA[k.hizala] || HIZA.sol} text-sm font-semibold text-gray-700 align-middle`}>
                        {k.ciz ? k.ciz(satir, i) : (hamDeger(k, satir) ?? '—')}
                      </td>
                    ))}
                  </tr>

                  {/* Açılır detay — tam genişlik. Kolon sayısı değişse de bozulmaz. */}
                  {detay && (
                    <tr className="bg-gray-50/60">
                      <td colSpan={kolonlar.length} className="px-6 py-5 border-b border-gray-100">
                        {detay}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })
          )}
        </tbody>

        {toplamGoster && gorunen.length > 0 && (
          <tfoot>
            <tr className="bg-gray-50 border-t-2 border-gray-100">
              {kolonlar.map((k, i) => (
                <td key={k.anahtar}
                  className={`${hucreDolgu} ${HIZA[k.hizala] || HIZA.sol} text-sm font-black text-gray-800`}>
                  {i === 0 ? toplamEtiketi
                    : typeof k.toplam === 'function' ? k.toplam(gorunen)
                    : k.toplam === true
                      ? gorunen.reduce((t, s) => t + (Number(hamDeger(k, s)) || 0), 0)
                          .toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                      : ''}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

/** Tablo hücrelerinde tekrar eden rozet görünümü. */
export function Rozet({ renk = 'gray', children }) {
  const renkler = {
    gray:    'bg-gray-100 text-gray-600 border-gray-200',
    violet:  'bg-violet-50 text-violet-600 border-violet-100',
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    amber:   'bg-amber-50 text-amber-600 border-amber-100',
    rose:    'bg-rose-50 text-rose-600 border-rose-100',
    indigo:  'bg-indigo-50 text-indigo-600 border-indigo-100',
    cyan:    'bg-cyan-50 text-cyan-600 border-cyan-100',
  };
  return (
    <span className={`inline-flex items-center px-3 py-1.5 rounded-xl border text-[10px]
                      font-black uppercase tracking-widest whitespace-nowrap ${renkler[renk] || renkler.gray}`}>
      {children}
    </span>
  );
}

/** Tablo hücrelerinde oran göstermek için ince çubuk. */
export function OranCubugu({ oran = 0, renk = 'violet', etiket }) {
  const renkler = {
    violet:  'bg-violet-500',
    emerald: 'bg-emerald-500',
    indigo:  'bg-indigo-500',
    rose:    'bg-rose-500',
    amber:   'bg-amber-500',
  };
  const yuzde = Math.max(0, Math.min(100, Number(oran) || 0));
  return (
    <div className="flex items-center gap-2.5 justify-end">
      <div className="w-20 h-1.5 rounded-full bg-gray-100 overflow-hidden flex-shrink-0">
        <div className={`h-full rounded-full ${renkler[renk] || renkler.violet}`} style={{ width: `${yuzde}%` }} />
      </div>
      <span className="text-xs font-black text-gray-600 tabular-nums">{etiket ?? `${yuzde.toFixed(1)}%`}</span>
    </div>
  );
}
